// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useConfig } from '@hooks/other/useConfig';
import React from 'react';
import {
  LocalStorageContext,
  LocalStorageContextProps,
  LocalStorageStateProps,
} from './LocalStorageContext';

interface LocalStorageProviderProps {
  children: React.ReactNode;
}

export const LocalStorageProvider: React.FC<LocalStorageProviderProps> = ({ children }) => {
  const config = useConfig();
  const [localStorageState, setLocalStorage] = React.useState({} as LocalStorageStateProps);

  const getStateFromLocalStorage = () => {
    const pollingEnabled = localStorage.getItem('pollingEnabled');
    const webSocketEnabled = localStorage.getItem('webSocketEnabled');

    const isDark = localStorage.getItem('isDark');
    const activeContractsLogsEnabled = localStorage.getItem(
      `activeContractsLogsEnabled_ver:${config.acs.configVersion}`,
    );

    setLocalStorage((p) => ({
      ...p,
      pollingEnabled: pollingEnabled === null || pollingEnabled === 'true',
      webSocketEnabled: webSocketEnabled === null || webSocketEnabled === 'true',
      isDark: isDark === null || isDark === 'true',
      activeContractsLogsEnabled:
        activeContractsLogsEnabled === null
          ? config.acs.enableLogsByDefault
          : activeContractsLogsEnabled === 'true',
    }));
  };
  const setLocalStorageState = React.useCallback(
    (key: string, value: string) => {
      if (key === 'pollingEnabled') {
        setLocalStorage((p) => ({
          ...p,
          pollingEnabled: value === 'true',
        }));
        localStorage.setItem(key, value);
      }
      if (key === 'webSocketEnabled') {
        setLocalStorage((p) => ({
          ...p,
          webSocketEnabled: value === 'true',
        }));
        localStorage.setItem(key, value);
      }
      if (key === 'isDark') {
        setLocalStorage((p) => ({
          ...p,
          isDark: value === 'true',
        }));
        localStorage.setItem(key, value);
      }

      if (key === 'activeContractsLogsEnabled') {
        setLocalStorage((p) => ({
          ...p,
          activeContractsLogsEnabled: value === 'true',
        }));
        localStorage.setItem(`activeContractsLogsEnabled_ver:${config.acs.configVersion}`, value);
      }
    },
    [config],
  );
  React.useEffect(() => {
    getStateFromLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localStorageState.pollingEnabled,
    localStorageState.webSocketEnabled,
    localStorageState.isDark,
    localStorageState.activeContractsLogsEnabled,
  ]);
  const value: LocalStorageContextProps = React.useMemo(
    () => ({
      localStorageState,
      setLocalStorageState,
    }),
    [setLocalStorageState, localStorageState],
  );
  return <LocalStorageContext.Provider value={value}>{children}</LocalStorageContext.Provider>;
};
